import { useMemo } from "react";
import { buildActionableSummary } from "../../utils/remediationCatalog.js";

/**
 * Grouped actionable remediation summary across open findings.
 *
 * @param {{ snapshot?: object, providerId?: string, variant?: 'dashboard' | 'report' }} props
 */
export default function ActionableSummaryPanel({ snapshot, providerId, variant = "dashboard" }) {
  const summary = useMemo(() => buildActionableSummary(snapshot, providerId), [snapshot, providerId]);
  const activeGroups = (summary?.groups || []).filter((g) => g.count > 0);
  if (!activeGroups.length) return null;

  const isReport = variant === "report";
  const sectionTitle = {
    fontSize: isReport ? 12 : 11,
    letterSpacing: isReport ? "0.12em" : "0.08em",
    textTransform: "uppercase",
    margin: 0,
    fontWeight: 700,
    color: isReport ? "var(--idaxis-print-muted, #334155)" : "var(--text-dim, #94a3b8)",
  };
  const groupLabel = {
    fontSize: 12,
    fontWeight: 700,
    margin: "0 0 6px",
    color: isReport ? "var(--idaxis-print-text, #0b1b34)" : "var(--text-primary)",
  };
  const itemMeta = {
    fontSize: isReport ? 12 : 11,
    color: isReport ? "var(--idaxis-print-muted, #334155)" : "var(--text-secondary)",
    lineHeight: 1.45,
  };
  const mono = {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: isReport ? 11 : 10,
  };

  return (
    <section style={{ marginTop: isReport ? 18 : 0, marginBottom: isReport ? 0 : 18 }}>
      <h3 style={sectionTitle}>Actionable summary</h3>
      {summary.caveat ? (
        <p
          style={{
            ...itemMeta,
            margin: "8px 0 0",
            fontStyle: "italic",
            fontSize: isReport ? 11 : 11,
          }}
        >
          {summary.caveat}
        </p>
      ) : null}
      <div
        style={{
          marginTop: 10,
          display: "grid",
          gap: 12,
          padding: isReport ? "14px 16px" : "12px 14px",
          border: `1px solid ${isReport ? "var(--idaxis-print-border, #e5e7eb)" : "var(--border)"}`,
          borderRadius: isReport ? 10 : 6,
          background: isReport ? "transparent" : "var(--bg-surface)",
        }}
      >
        {activeGroups.map((group) => (
          <div key={group.urgency_key}>
            <div style={groupLabel}>
              {group.urgency_label}{" "}
              <span style={{ fontWeight: 500, color: itemMeta.color }}>({group.count})</span>
            </div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
              {group.findings.map((f) => (
                <li
                  key={f.rule_id || f.title}
                  style={{
                    padding: isReport ? "8px 0" : "8px 10px",
                    borderRadius: 4,
                    background: isReport ? "transparent" : "var(--bg-elevated)",
                    borderBottom: isReport ? "1px solid var(--idaxis-print-border, #e5e7eb)" : "none",
                  }}
                >
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "baseline" }}>
                    {f.rule_id ? <span style={mono}>{f.rule_id}</span> : null}
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: isReport ? 13 : 12,
                        color: isReport ? "var(--idaxis-print-text, #0b1b34)" : "var(--text-primary)",
                      }}
                    >
                      {f.title || "Finding"}
                    </span>
                  </div>
                  <p style={{ ...itemMeta, margin: "4px 0 0" }}>
                    {f.remediation?.remediation_summary || "—"}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
